class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    /* Input-aware debounce (perf pass 2026-08-19): filter CLICKS (checkboxes,
       selects) and programmatic commits (price slider / sort dropdown dispatch
       synthetic events, isTrusted=false) fire after a short coalescing beat -
       the old flat 800ms was pure dead air on every click (skeleton + fetch
       only started 0.8s after the tick). Only real TYPING in the price boxes
       keeps a long settle so we don't fetch per keystroke. Two timers can
       overlap in theory (tick then type); the renderPage seq guard makes the
       stale response paint-safe. */
    this.debouncedOnSubmitFast = debounce((event) => {
      this.onSubmitHandler(event);
    }, 120);
    this.debouncedOnSubmitSlow = debounce((event) => {
      this.onSubmitHandler(event);
    }, 600);
    this.debouncedOnSubmit = (event) => {
      const t = event.target;
      const typing = event.isTrusted && t && t.tagName === 'INPUT' && (t.type === 'text' || t.type === 'number');
      /* instant-response UI (Chris 2026-08-19): the skeleton flips on AT THE
         CLICK, not after the debounce beat - the page visibly reacts the
         moment a filter is ticked. Typing keeps the page as-is until the
         settle (a skeleton per keystroke would strobe). */
      if (!typing) FacetFiltersForm.showLoading();
      (typing ? this.debouncedOnSubmitSlow : this.debouncedOnSubmitFast)(event);
    };

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /* skeleton on, CROSS-FADE (Chris 2026-08-19): .ac-swap shows both layers
     at once - live cards fading out, skeleton (absolutely overlaid in its
     final spot) fading in over them - then ~160ms later the settle swaps to
     .loading, which is a pure display flip in identical geometry. No white
     trough. The settle only lands if the SAME .collection node is still
     connected: a cached/prefetched render replaces the container first, so
     instant results skip the skeleton entirely (cards -> new cards stagger).
     Null-safe - the 0-result empty state has no .collection (filtering out
     of it used to throw here and go completely dead). */
  static showLoading() {
    const container = document.getElementById('ProductGridContainer');
    const coll = container && container.querySelector('.collection');
    if (!coll || coll.classList.contains('loading')) return;
    coll.classList.add('ac-swap');
    clearTimeout(FacetFiltersForm.loadingTimer);
    FacetFiltersForm.loadingTimer = setTimeout(() => {
      if (coll.isConnected) {
        coll.classList.remove('ac-swap');
        coll.classList.add('loading');
      }
    }, 160);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    document.dispatchEvent(new CustomEvent('bsd:filters-loading'));
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    FacetFiltersForm.showLoading();
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    /* seq = stale-response guard: with the short debounce, two quick clicks
       can have both fetches in flight - only the LATEST render may paint
       (an older response landing second used to win the grid). */
    const seq = ++FacetFiltersForm.renderSeq;
    // Desktop filter changes return to the page top after the new results render.
    // History navigation keeps the browser's normal scroll restoration.
    FacetFiltersForm.scrollToTopSeq =
      updateURLHash && document.querySelector('.bsd-collection-reference') &&
      window.matchMedia('(min-width: 981px)').matches ? seq : null;
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, seq);
    });


  }

  /* single fetch pipeline, deduped by URL: a hover-prefetch and the click's
     render never fetch the same section twice - the click just joins the
     in-flight promise (or hits the filterData cache). Every completed fetch
     lands in filterData for back/forward + repeat clicks. */
  /* a usable section response must carry the swap targets; anything else
     (challenge page, 404, error page, truncated body) must NEVER be cached
     or painted - painting it used to strip the whole rail + grid (the
     "white screen": renderFilters removed every group the response lacked). */
  static sectionResponseValid(html) {
    return (
      typeof html === 'string' &&
      html.indexOf('id="ProductGridContainer"') !== -1 &&
      html.indexOf('js-filter') !== -1
    );
  }

  static fetchSection(url) {
    FacetFiltersForm.inflight = FacetFiltersForm.inflight || {};
    if (FacetFiltersForm.inflight[url]) return FacetFiltersForm.inflight[url];
    const p = fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (FacetFiltersForm.sectionResponseValid(responseText)) {
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        }
        delete FacetFiltersForm.inflight[url];
        return responseText;
      })
      .catch((error) => {
        delete FacetFiltersForm.inflight[url];
        throw error;
      });
    FacetFiltersForm.inflight[url] = p;
    return p;
  }

  /* hover-intent / pointerdown prefetch hook (wired by atelier-collection.js):
     warms the cache for the section render a click WOULD trigger. searchParams
     must be serialized exactly like onSubmitHandler's (same forms, same order)
     or the cache key won't match. Fire-and-forget; errors are irrelevant here
     because the real click still runs its own fetch path. */
  static prefetchParams(searchParams) {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const url = `${window.location.pathname}?section_id=${grid.dataset.id}&${searchParams}`;
    if (FacetFiltersForm.filterData.some((element) => element.url === url)) return;
    FacetFiltersForm.fetchSection(url).catch(() => {});
  }

  /* an unusable response (or a dead network) on the CURRENT render intent
     falls back to a real navigation - the filter still applies server-side
     and the user is never left staring at a stripped page. Stale responses
     (superseded by a newer click) just evaporate. */
  static bailToNavigation() {
    const qs = FacetFiltersForm.searchParamsPrev || '';
    window.location.href = `${window.location.pathname}${qs ? '?'.concat(qs) : ''}`;
  }

  static renderSectionFromFetch(url, event, seq) {
    FacetFiltersForm.fetchSection(url)
      .then((html) => {
        /* stale-response guard: never paint a superseded response over a newer one
           (the cache write above still happened) */
        if (seq !== undefined && seq !== FacetFiltersForm.renderSeq) return;
        if (!FacetFiltersForm.sectionResponseValid(html)) {
          FacetFiltersForm.bailToNavigation();
          return;
        }
        FacetFiltersForm.renderSections(html, event);
      })
      .catch(() => {
        if (seq === undefined || seq === FacetFiltersForm.renderSeq) FacetFiltersForm.bailToNavigation();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSections(html, event);
  }

  /* one parse for everything (was 3 full DOMParser passes per render).
     ONLY the grid swap is load-bearing: if IT throws, the outer catch bails
     to a real navigation. Every other step is enhancement and is isolated -
     an exception in a counts/pills/mobile-hooks step must never cascade into
     the bail (which would silently turn EVERY filter click into a full page
     reload: no skeleton, flash of old page, exactly the sluggish feel the
     AJAX path exists to avoid). */
  static renderSections(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    try { FacetFiltersForm.renderFilters(parsedHTML, event); } catch (e) {}
    try { FacetFiltersForm.renderProductCount(parsedHTML); } catch (e) {}
    try {
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('bsd:filters-ready'));
    if (FacetFiltersForm.scrollToTopSeq === FacetFiltersForm.renderSeq) {
      FacetFiltersForm.scrollToTopSeq = null;
      // Run after filter focus is restored so focus cannot scroll back down.
      if (window.matchMedia('(min-width: 981px)').matches) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    }
  }

  static renderProductGridContainer(parsedHTML) {
    document.getElementById('ProductGridContainer').innerHTML =
      parsedHTML.getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(parsedHTML) {

    if (!document.getElementById('ProductCount')) return;

    const count = parsedHTML.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        /* PRICE GROUP: swapping its markup tears down the live slider and
           rebuilds it ~60ms later (visible flicker on EVERY filter click).
           When the committed gte/lte values are unchanged, keep the working
           slider DOM - the track is deliberately frozen while refining
           (stability doctrine, mirrors /search). A real price change (pill
           removed, clear all) still swaps and re-inits. */
        const priceVal = (el, name) => {
          const input = el.querySelector(`input[name="filter.v.price.${name}"]`);
          if (!input) return null;
          /* normalize: the slider writes "800", the server echoes
             money_without_currency ("800.00", "2,500.00") - compare as numbers */
          const raw = String(input.value).replace(/[^0-9.]/g, '');
          return raw === '' ? '' : String(parseFloat(raw));
        };
        if (
          currentElement.querySelector('[data-ac-price-slider]') &&
          priceVal(currentElement, 'gte') === priceVal(elementToRender, 'gte') &&
          priceVal(currentElement, 'lte') === priceVal(elementToRender, 'lte')
        ) {
          return;
        }
        /* unchanged group (common on sort-only renders): skip the repaint */
        if (currentElement.innerHTML === elementToRender.innerHTML) return;
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // re‑attach mobile facet click handlers after DOM update
    mobileFacetsClick();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile-filter', '.active-facets-mobile', '.active-facets-desktop'];

    /* activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    }); */

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElementFromFetch = html.querySelector(selector);
      if (!activeFacetsElementFromFetch) return;
      
      // Update ALL matching elements in the DOM, not just the first one
      const activeFacetsElementsInDOM = document.querySelectorAll(selector);
      activeFacetsElementsInDOM.forEach((element) => {
        element.innerHTML = activeFacetsElementFromFetch.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector) || !document.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    /* guarded: a missing mobile form / unupgraded menu-drawer must not throw
       (an exception here used to look like a total render failure) */
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileForm && mobileForm.closest('menu-drawer');
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') mobileDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const next = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    if (next !== window.location.pathname + window.location.search) history.pushState({ searchParams }, '', next);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderSeq = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// bind mobile facet click behaviour on first load
mobileFacetsClick();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// mobile facets trigger mobile click - using event delegation
function mobileFacetsClick() {
  // Only bind once using a flag
  if (window.mobileFacetsClickBound) return;
  window.mobileFacetsClickBound = true;

  document.addEventListener('click', (event) => {
    // const facet = event.target.closest('.mobile-facets__item .facet-checkbox__text-label');
    const facet = event.target.closest('.mobile-facets__item');
    if (!facet) return;

    // console.log('mobile facet clicked');
    
    // find sibling input
    // const swatchWrapper = facet.closest('.mobile-facets__label').querySelector('.swatch-input-wrapper label');
    const swatchWrapper = facet.querySelector('.swatch-input-wrapper > label');

    // console.log('swatchWrapper:', swatchWrapper);

    // click the input
    if (swatchWrapper) swatchWrapper.click();

    // close filter
    const closeButton = document.querySelector('.mobile-facets__close');
    if (closeButton) closeButton.click();
  });
}

// keep sort dropdowns (desktop + mobile) in sync and trigger facet reload
function initFilterSortSync() {
  document.addEventListener('change', function (e) {
    const target = e.target;
    if (!target.matches('#filter_sort, #SortBy')) return;

    const value = target.value;
    const allSortInputs = document.querySelectorAll('#filter_sort, #SortBy');

    // sync values both ways
    allSortInputs.forEach((input) => {
      if (input.value !== value) {
        input.value = value;
      }
    });

    // now mimic what FacetFiltersForm.onSubmitHandler() does for sort changes
    const facetFiltersEl = document.querySelector('facet-filters-form');
    if (!facetFiltersEl) return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const isMobile = target.closest('form')?.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(facetFiltersEl.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(facetFiltersEl.createSearchParams(form));
      }
    });

    facetFiltersEl.onSubmitForm(forms.join('&'), e);
  });
}

// call once on page load
document.addEventListener('DOMContentLoaded', function () {
  initFilterSortSync();
});
